<template>
	<v-form
		class="login-form"
		ref="form"
		v-model="valid"
		lazy-validation
		@submit.prevent="validateAndSubmit"
	>
		<v-row>
			<v-col cols="12">
				<v-text-field
					class="pt-0"
					color="red"
					v-model="fields.login"
					:error-messages="field_errors.email"
					:rules="rules.email"
					required
					:placeholder="placeHolderLogin"
				>
				</v-text-field>
			</v-col>
			<v-col cols="12" class="pb-0">
				<v-text-field
					class="pt-0 password-field"
					:append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
					:type="showPassword ? 'text' : 'password'"
					v-model="fields.password"
					required
					:rules="rules.password"
					:error-messages="field_errors.password"
					:placeholder="placeHolderPassword"
					@click:append="showPassword = !showPassword"
				>
				</v-text-field>
				<v-checkbox color="#33a1dc" class="remember-me" label="Remember me"></v-checkbox>
			</v-col>
			<p
				class="text-center red--text"
				v-for="error in errorList"
				:key="error"
				style="width: 100%"
			>
				{{ error }}
			</p>
			<v-col cols="12" class="text-center mb-4">
				<v-btn
					class="align-center px-11 py-6 login-form__submit"
					type="submit"
					:loading="isLoading"
				>
					{{loginButtonName}}
				</v-btn>
				<p class="terms-of-use" v-html="policyAndTermText"></p>
			</v-col>
			<v-col cols="12">
				<v-row
					v-if="isForgotPasswordEnable"
					no-gutters class="justify-center"
					@click=""
				>
					<a
						:href="forgotPasswordExternalUrl"
						target='_blank'
						class="text-right login-form__link"
					>{{forgotPasswordTitle}}</a>
				</v-row>
			</v-col>
		</v-row>
	</v-form>
</template>

<script>
import Form from '../../mixins/Form'
import { mapActions, mapGetters } from 'vuex'
import { ruleEmail, rulePassword, ruleRequired } from '@/utils/rules'

export default {
	name: 'LoginForm',
	mixins: [Form],
	data: () => ({
		fields: {
			login: '',
			password: '',
		},
		showPassword: false,
	}),
	computed: {
		...mapGetters('auth', ['authErrors', 'isLoading']),
		...mapGetters('settings', ['login', 'baseCore']),

		rules() {
			return {
				email: [
					(v) => ruleRequired(v) || 'Field is required.'
				],
				password: [
					(v) => ruleRequired(v) || 'Field is required.',
					(v) => rulePassword(v) || 'Password must contain a minimum of 5 characters.',
				],
			}
		},
		errorList() {
			if (this.authErrors.length <= 0) {
				return []
			}

			return [...new Set(this.authErrors)].filter(item => item)
		},
		loginButtonName() {
			const {
				login_button_name: loginButton
			} = this.login || {}

			return loginButton || 'Login'
		},
		placeHolderLogin() {
			const {
				login_field_title: loginPlaceHolder
			} = this.login || {}

			return loginPlaceHolder || 'Login'
		},
		placeHolderPassword() {
			const {
				password_field_title: passwordPlaceHolder
			} = this.login || {}

			return passwordPlaceHolder || 'Password'
		},
		isForgotPasswordEnable() {
			const {
				external_url: externalUrl,
				forgot_password_enabled: forgotPasswordEnable
			} = this.login || {}

			return !!externalUrl && !!forgotPasswordEnable
		},
		forgotPasswordTitle() {
			const {
				forgot_password_title: forgotPasswordTitle
			} = this.login || {}

			return forgotPasswordTitle || 'Forgot password'
		},
		forgotPasswordExternalUrl() {
			const {
				forgot_password_url: forgotPasswordUrl
			} = this.login || {}

			return forgotPasswordUrl || ''
		},
		policyAndTermText() {
			const {
				license_text: licenseText,
				login_button_name: loginButtonName
			} = this.login || {}

			const {
				privacy_policy: policy,
				terms_of_use: termOfUse
			} = this.baseCore || {}

			return licenseText
				.replace(/{%login.login_button_name%}/g, loginButtonName)
				.replace(/{%core.privacy_policy%}/g, policy)
				.replace(/{%core.terms_of_use%}/g, termOfUse)
				.replace(/href/g, 'target="_blank"href')
		}
	},
	methods: {
		...mapActions('auth', {
			loginHandle: 'login',
			signInWithGoogle: 'signInWithGoogle',
			signInWithFacebook: 'signInWithFacebook',
			signInWithApple: 'signInWithApple',
		}),
		submitForm() {
			return this.loginHandle(this.fields)
		},
		navigateToRegisterPage() {
			this.$router.push({ name: 'register' })
		}
	},
}
</script>

<style scoped lang="scss">
.login-form {
	font-family: Montserrat, system-ui;

	.remember-me {
		.v-label {
			font-style: normal;
			font-weight: 500;
			font-size: 15px;
		}
	}

	.theme--light.v-input input {
		color: #000;
	}

	.v-text-field {
		input {
			padding: 10px 0 20px;
			font-size: 18px;
			font-weight: 500;
			max-height: 50px;
		}
	}

	.theme--light.v-text-field > .v-input__control > .v-input__slot:before {
		color: #e5e5e5;
		border-color: #e5e5e5;
	}

	.password-field {
		.v-input__append-inner {
			margin-top: 0;
			.v-input__icon--append {
				.v-icon {
					color: #e5e5e5;
					padding: 30px 0 20px;
				}
			}
		}
	}

	input::placeholder {
		color: #e5e5e5 !important;
		opacity: 1;
	}

	&__description {
		font-weight: bold;
		font-size: 18px;
	}

	&__link {
		font-style: normal;
		font-weight: 500;
		font-size: 14px;
		color: var(--text_button_tint) !important;
		text-decoration: underline;
	}

	&__social {
		p {
			font-weight: 500;
			font-size: 14px;
			line-height: 17px;
		}

		button {
			width: 61px;
			height: 61px !important;
			box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
			border-radius: 12px;
		}
	}

	button[type='submit'] {
		border-radius: 8px;
		font-style: normal;
		font-weight: bold;
		font-size: 16px;
		text-transform: none;
		background-color: var(--button_main_background) !important;
		color: var(--button_main_text_color) !important;
		max-width: 420px;
		width: 100%;
	}
	.terms-of-use {
		padding-top: 20px;
		max-width: 420px;
		width: 100%;
		color: #a0a0a0 !important;
		font-size: 12px;
		font-family: Montserrat, sans-serif;
		font-weight: 500;
		text-align: left;

		a {
			color: var(--link) !important;
		}
	}
}
</style>
